/**
 * Equity charge estimator — mirrors backend (Zerodha Intraday default / CNC optional).
 */
function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

const NSE_TXN_RATE = 0.0000307;
const SEBI_RATE = 0.000001;

let _product = 'intraday';

export function setChargeProduct(product) {
  const p = String(product || '').toLowerCase();
  _product = (p === 'cnc' || p === 'delivery') ? 'delivery' : 'intraday';
}

export function setChargeProductFromOrderType(orderType) {
  const t = String(orderType || '').toUpperCase();
  setChargeProduct(t === 'CNC' || t === 'DELIVERY' || t === 'NRML' ? 'delivery' : 'intraday');
}

function sideChargesIntraday(turnover, side = 'BUY') {
  const v = Math.max(0, Number(turnover) || 0);
  if (v <= 0) {
    return { side: String(side || 'BUY').toUpperCase(), turnover: 0, brokerage: 0, stt: 0, exchange: 0, sebi: 0, stampDuty: 0, dp: 0, gst: 0, total: 0 };
  }
  const sideUp = String(side || 'BUY').toUpperCase();
  const brokerage = Math.min(20, v * 0.0003);
  const stt = sideUp === 'SELL' ? Math.round(v * 0.00025) : 0;
  const exchange = v * NSE_TXN_RATE;
  const sebi = v * SEBI_RATE;
  const stampDuty = sideUp === 'BUY' ? Math.round(v * 0.00003) : 0;
  const gst = 0.18 * (brokerage + exchange + sebi);
  const total = brokerage + stt + exchange + sebi + stampDuty + gst;
  return {
    side: sideUp,
    turnover: round2(v),
    brokerage: round2(brokerage),
    stt: round2(stt),
    exchange: round2(exchange),
    sebi: round2(sebi),
    stampDuty: round2(stampDuty),
    dp: 0,
    gst: round2(gst),
    total: round2(total)
  };
}

function sideChargesDelivery(turnover, side = 'BUY') {
  const v = Math.max(0, Number(turnover) || 0);
  if (v <= 0) {
    return { side: String(side || 'BUY').toUpperCase(), turnover: 0, brokerage: 0, stt: 0, exchange: 0, sebi: 0, stampDuty: 0, dp: 0, gst: 0, total: 0 };
  }
  const sideUp = String(side || 'BUY').toUpperCase();
  const brokerage = 0;
  const stt = v * 0.001;
  const exchange = v * NSE_TXN_RATE;
  const sebi = v * SEBI_RATE;
  const stampDuty = sideUp === 'BUY' ? v * 0.00015 : 0;
  const dp = sideUp === 'SELL' ? 15.93 : 0;
  const gst = 0.18 * (brokerage + exchange + sebi);
  const total = brokerage + stt + exchange + sebi + stampDuty + dp + gst;
  return {
    side: sideUp,
    turnover: round2(v),
    brokerage: round2(brokerage),
    stt: round2(stt),
    exchange: round2(exchange),
    sebi: round2(sebi),
    stampDuty: round2(stampDuty),
    dp: round2(dp),
    gst: round2(gst),
    total: round2(total)
  };
}

export function sideCharges(turnover, side = 'BUY') {
  return _product === 'delivery'
    ? sideChargesDelivery(turnover, side)
    : sideChargesIntraday(turnover, side);
}

export function roundTripCharges(buyValue, sellValue) {
  const buy = sideCharges(buyValue, 'BUY');
  const sell = sideCharges(sellValue, 'SELL');
  return {
    buy,
    sell,
    totalCharges: round2(buy.total + sell.total)
  };
}

export function cardBrokerCharges({ buyPrice, sellPrice, qty }) {
  const q = Math.max(1, Number(qty) || 1);
  const buyPx = Number(buyPrice) || 0;
  const sellPx = Number(sellPrice) || buyPx;
  const buyValue = buyPx > 0 ? round2(buyPx * q) : 0;
  const sellValue = sellPx > 0 ? round2(sellPx * q) : 0;
  const buy = sideCharges(buyValue, 'BUY');
  const sell = sideCharges(sellValue, 'SELL');
  return {
    qty: q,
    buyPrice: buyPx,
    sellPrice: sellPx,
    buyValue,
    sellValue,
    buy,
    sell,
    buyTotal: buy.total,
    sellTotal: sell.total,
    buyPerShare: buyPx > 0 ? round2(buy.total / q) : 0,
    sellPerShare: sellPx > 0 ? round2(sell.total / q) : 0,
    roundTrip: round2(buy.total + sell.total),
    buyTip: `Brokerage ₹${buy.brokerage} · STT ₹${buy.stt} · stamp ₹${buy.stampDuty} · GST ₹${buy.gst} · total ₹${buy.total}`,
    sellTip: `Brokerage ₹${sell.brokerage} · STT ₹${sell.stt} · DP ₹${sell.dp} · GST ₹${sell.gst} · total ₹${sell.total}`
  };
}

export { round2 };
